import React, { useEffect, useRef } from "react";
import { Animated, PanResponderGestureState, View } from "react-native";
import { Draggable, DropZone } from "../../../../../../components/DragDrop";
import { isClonableElement } from "@towersystems/roam-common-native/lib/utilities/components";
import { useDragDropContext } from "../../../../../../utilities/drag-drop";
import { PageItemType } from "../../../../../../utilities/layout";
import { Vector2D } from "../../../../../../utilities/vector";
import { useLayoutProvider } from "../../../../utils/layout";
import { JiggleView } from "../../../JiggleView";
import { ActivationMethod, EnhancedEventDetection } from "./components";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";

export type InteractivityWrapperProps = {
  draggable?: boolean;
  droppable?: boolean;
  dragging?: boolean;
  item: PageItemType;
  onPress?: () => void;
  onDrop?: (...args: any[]) => void;
  onEnter?: () => void;
  onLeave?: () => void;
  children?: React.ReactNode;
};

export function InteractivityWrapper({
  item,
  draggable = false,
  droppable = false,
  dragging = false,
  onPress = () => null,
  onDrop = () => null,
  onEnter = () => null,
  onLeave = () => null,
  children,
}: InteractivityWrapperProps) {
  const online = useOnline();

  const {
    controllers: { animationController, dragController, dropController },
    draggingComponentRef,
    pointerRef,
  } = useDragDropContext();

  const tileRefs = useRef<any[]>([]);
  const ref = useRef<any>(null);

  const animationRegistryRef = useRef(animationController.animationRegistry);

  const { isEditing, startEditing, stopEditing } = useLayoutProvider();

  function handleOnDrop(data: any) {
    if (onDrop) {
      onDrop(data);
    }
  }

  const hasAnimatedValue = animationRegistryRef.current?.has(item.id);

  const animatedValue = hasAnimatedValue
    ? animationRegistryRef.current?.get(item.id)
    : undefined;

  const isDraggedComponent = draggingComponentRef.current?.data?.id === item.id;

  const jiggle = false; // isEditing && !item.placeholder; jiggling is a luxury low end devices cannot afford

  const reffedChildren = React.Children.map(children, (child, index) => {
    if (isClonableElement(child)) {
      return React.cloneElement(child, {
        ref: (el: any) => (tileRefs.current[index] = el),
      });
    } else {
      return child;
    }
  });

  const content = (
    <Animated.View style={animatedValue?.getLayout()}>
      <JiggleView jiggle={jiggle}>{reffedChildren}</JiggleView>
    </Animated.View>
  );

  let markup = content;

  if (isEditing) {
    if (draggable) {
      markup = (
        <Draggable key={item.position} ref={ref} data={item}>
          {content}
        </Draggable>
      );
    }

    if (droppable && dragging && !isDraggedComponent) {
      markup = (
        <DropZone
          key={item.position}
          id={`${item.position}`}
          onDrop={handleOnDrop}
          onEnter={onEnter}
          onLeave={onLeave}
        >
          {content}
        </DropZone>
      );
    }
  }

  function handleActivate() {
    if (!online) return;
    startEditing();
    dragController.onDrag(ref.current, children, item);
  }

  function handleMove(e: PanResponderGestureState) {
    const draggingAnimatedValueXY = getDraggingAnimatedValueXY();

    if (!draggingAnimatedValueXY) return;

    const draggingCoordMapping = {
      x: draggingAnimatedValueXY.x,
      y: draggingAnimatedValueXY.y,
    };

    const delta = Vector2D.create({
      x: e.dx,
      y: e.dy,
    });

    Animated.event([draggingCoordMapping], { useNativeDriver: true })(
      delta.toComponents()
    );

    const pointerRefMapping = {
      x: pointerRef.current!.x,
      y: pointerRef.current!.y,
    };

    const origin = Vector2D.create({
      x: e.x0,
      y: e.y0,
    });

    const absolutePosition = delta.add(origin);

    Animated.event([pointerRefMapping], { useNativeDriver: true })(
      absolutePosition.toComponents()
    );
  }

  function getDraggingAnimatedValueXY() {
    const draggingComponentId = draggingComponentRef.current?.data.config.id;

    if (!draggingComponentId || !pointerRef.current) return;

    return animationRegistryRef.current.get(draggingComponentId);
  }

  function handleMarkupPress() {
    tileRefs.current.forEach((tile) => {
      tile?.tilePress?.();
    });
  }

  useEffect(() => {
    if (!online && isEditing) {
      stopEditing();
    }
  }, [online, isEditing]);

  if (!draggable) {
    return markup;
  }

  return (
    <EnhancedEventDetection
      onActivate={handleActivate}
      onMove={handleMove}
      onDeactivate={() => dropController.handleDrop(item)}
      onPress={() => {
        if (isEditing) {
          onPress();
        } else {
          handleMarkupPress();
        }
      }}
      activationMethod={ActivationMethod.ON_LONG_PRESS}
    >
      {markup}
    </EnhancedEventDetection>
  );
}
